<template>
    <el-dialog
        title="Bulk Set Allocation"
        :visible.sync="visible"
        :fullscreen="true"
        :close-on-click-modal="false"
        :close-on-press-escape="!submitting"
        :show-close="!submitting"
        append-to-body
        class="text-left"
        @opened="createSpreadsheet"
        @closed="destroySpreadsheet"
    >
        <div v-if="errorMessage" class="alert alert-danger mb-2">{{ errorMessage }}</div>

        <div v-loading="submitting" class="bulk-allocation-sheet" style="width: 100%;">
            <div ref="spreadsheet" style="width: 100%;"></div>
        </div>
        <template slot="footer">
            <div class="d-flex align-items-center justify-content-between w-100">
                <div class="d-flex align-items-center">
                
                    <mdb-btn color="elegant" size="sm" :disabled="!canSave" @click="submit">
                    <span v-if="submitting || checking || pendingLookups" class="spinner-border spinner-border-sm mr-1"></span>
                    {{ submitting ? 'Saving...' : (checking || pendingLookups ? 'Validating items...' : 'Save Allocations') }}
                    </mdb-btn>
                    <mdb-btn color="grey" size="sm" :disabled="submitting" @click="clearForm">Clear</mdb-btn>
                    <mdb-btn color="grey" size="sm" :disabled="submitting" @click="visible = false">Close</mdb-btn>
                </div>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { mdbBtn } from 'mdbvue'
import { MessageBox } from 'element-ui'
import jspreadsheet from '../../jsexcel/jexcel'

export default {
    name: 'BulkAllocationDialog',
    components: { mdbBtn },
    data() {
        return {
            visible: false,
            submitting: false,
            checking: false,
            errorMessage: '',
            rows: [],
            lookups: {},
            saveMessages: {},
            activeLookups: 0,
            session: 0,
            refreshTimer: null,
            rendering: false,
            spreadsheet: null,
            selectedRowCount: 0,
        }
    },
    computed: {
        pendingLookups() {
            return this.rows.some(row => row.lookup && ['queued', 'loading'].includes(row.lookup.status))
        },
        canSave() {
            return !this.submitting && !this.checking && !this.pendingLookups &&
                this.rows.length > 0 && this.rows.length <= 500
        },
    },
    mounted() {
        if (this.visible) this.createSpreadsheet()
    },
    beforeDestroy() {
        this.destroySpreadsheet()
    },
    methods: {
        ...mapActions('InventoryItemMasterModule', ['bulkAllocation', 'getSetAllocationItem']),
        openDialog() {
            this.destroySpreadsheet()
            this.visible = true
        },
        resetLookups() {
            this.session++
            clearTimeout(this.refreshTimer)
            this.refreshTimer = null
            this.lookups = {}
            this.saveMessages = {}
            this.activeLookups = 0
            this.checking = false
            this.errorMessage = ''
            this.rows = []
            this.selectedRowCount = 0
        },
        createSpreadsheet() {
            if (!this.$refs.spreadsheet || this.spreadsheet || !this.visible) return
            this.spreadsheet = jspreadsheet(this.$refs.spreadsheet, {
                data: [],
                columns: [
                    { type: 'text', title: 'ITEM CODE', name: 'series', width: 220 },
                    { type: 'text', title: 'ITEM NAME', name: 'name', width: 300, readOnly: true },
                    { type: 'numeric', title: 'EXISTING REMAINING ALLOCATED QTY', name: 'existing_quantity', width: 280, readOnly: true },
                    { type: 'numeric', title: 'MINIMUM QTY', name: 'minimum_quantity', width: 160, readOnly: true },
                    { type: 'numeric', title: 'NEW TOTAL ALLOCATION QTY', name: 'allocated_quantity', width: 240 },
                    { type: 'text', title: 'RESPONSE', name: 'message', width: 380, readOnly: true },
                ],
                minDimensions: [6, 50],
                tableWidth: '100%',
                tableHeight: 'calc(100vh - 235px)',
                tableOverflow: true,
                defaultColAlign: 'left',
                columnSorting: false,
                parseFormulas: false,
                allowCopy: true,
                allowDeleteColumn: false,
                allowDeleteRow: false,
                allowInsertColumn: false,
                allowRenameColumn: false,
                contextMenu: () => false,
                onselection: () => {
                    this.selectedRowCount = this.spreadsheet ? this.spreadsheet.getSelectedRows(true).length : 0
                },
                onbeforechange: (instance, cell, column, row) => {
                    if (this.submitting) return this.spreadsheet.getValueFromCoords(column, row)
                },
                onbeforeinsertrow: (instance, row, count) => {
                    if (this.submitting || (this.spreadsheet && this.spreadsheet.getData().length + count > 500)) return false
                },
                onafterchanges: () => this.scheduleRefresh(),
                oninsertrow: () => this.scheduleRefresh(),
            })
        },
        scheduleRefresh() {
            if (this.rendering || this.submitting || !this.spreadsheet) return
            this.saveMessages = {}
            this.errorMessage = ''
            this.checking = true
            clearTimeout(this.refreshTimer)
            this.refreshTimer = setTimeout(() => this.refreshRows(), 150)
        },
        refreshRows() {
            if (!this.spreadsheet || !this.visible) return
            clearTimeout(this.refreshTimer)
            this.checking = false
            const data = this.spreadsheet.getJson()
            const rows = []
            data.forEach((entry, index) => {
                const series = String(entry.series == null ? '' : entry.series).trim()
                const quantity = String(entry.allocated_quantity == null ? '' : entry.allocated_quantity).trim()
                if (!series && !quantity) return
                const key = series.toUpperCase()
                if (series && !this.lookups[key] && rows.length < 500) {
                    this.$set(this.lookups, key, { series, status: 'queued', item: null, message: '' })
                }
                rows.push({ index, series, key, quantity, lookup: this.lookups[key], valid: false })
            })
            const codeCounts = {}
            const idCounts = {}
            rows.forEach(row => {
                codeCounts[row.key] = (codeCounts[row.key] || 0) + 1
                const item = row.lookup && row.lookup.item
                if (item) idCounts[item.id] = (idCounts[item.id] || 0) + 1
            })
            rows.forEach(row => {
                const lookup = row.lookup
                const item = lookup && lookup.item
                if (rows.length > 500) row.message = 'Maximum 500 items per submission.'
                else if (!row.series) row.message = 'Item Code is required.'
                else if (codeCounts[row.key] > 1 || (item && idCounts[item.id] > 1)) row.message = 'Duplicate item. Enter each item once.'
                else if (!lookup || ['queued', 'loading'].includes(lookup.status)) row.message = 'Checking item...'
                else if (lookup.status === 'error') row.message = lookup.message
                else if (!item || item.minimum_allocation_quantity == null || !Number.isSafeInteger(Number(item.minimum_allocation_quantity)) || Number(item.minimum_allocation_quantity) < 0) row.message = 'Minimum Qty unavailable. Validate Items again before saving.'
                else if (!/^\d+$/.test(row.quantity) || !Number.isSafeInteger(Number(row.quantity))) row.message = 'Enter a whole-number New Total Allocation Qty (0 or greater).'
                else if (Number(row.quantity) < Number(item.minimum_allocation_quantity)) row.message = `New Total Allocation Qty cannot be less than Minimum Qty (${item.minimum_allocation_quantity} already booked).`
                else {
                    row.valid = true
                    row.message = 'Ready'
                }
            })
            this.rows = rows
            this.renderRows(data.length)
            this.runLookups()
        },
        renderRows(length) {
            const sheet = this.spreadsheet
            const previousEvents = sheet.ignoreEvents
            const previousHistory = sheet.ignoreHistory
            this.rendering = true
            sheet.ignoreEvents = true
            sheet.ignoreHistory = true
            try {
                const byIndex = {}
                this.rows.forEach(row => { byIndex[row.index] = row })
                for (let index = 0; index < length; index++) {
                    const row = byIndex[index]
                    const item = row && row.lookup && row.lookup.item
                    const result = row && this.saveMessages[index]
                    this.writeCell(1, index, item ? item.name : '')
                    this.writeCell(2, index, item ? item.existing_remaining_allocated_quantity : '')
                    this.writeCell(3, index, item ? item.minimum_allocation_quantity : '')
                    this.writeCell(5, index, result ? result.message : (row ? row.message : ''))
                    const color = result ? (result.success ? '#00c851' : '#fb3') : '#fff'
                    sheet.setStyle('F' + (index + 1), 'background-color', color, true, true)
                }
            } finally {
                sheet.ignoreEvents = previousEvents
                sheet.ignoreHistory = previousHistory
                this.rendering = false
            }
        },
        writeCell(column, row, value) {
            const normalized = value == null ? '' : value
            if (String(this.spreadsheet.getValueFromCoords(column, row)) !== String(normalized)) {
                this.spreadsheet.setValueFromCoords(column, row, normalized, true)
            }
        },
        runLookups() {
            const session = this.session
            const needed = new Set(this.rows.map(row => row.key))
            Object.keys(this.lookups).forEach(key => {
                const lookup = this.lookups[key]
                if (this.activeLookups >= 4 || lookup.status !== 'queued' || !needed.has(key)) return
                lookup.status = 'loading'
                this.activeLookups++
                this.getSetAllocationItem(lookup.series).then(item => {
                    if (session !== this.session) return
                    lookup.item = item
                    lookup.status = item ? 'ready' : 'error'
                    lookup.message = item ? '' : 'No active Set item found for this code.'
                }).catch(error => {
                    if (session !== this.session) return
                    lookup.status = 'error'
                    lookup.message = this.errorText(error)
                }).finally(() => {
                    if (session !== this.session) return
                    this.activeLookups--
                    this.refreshRows()
                })
            })
        },
        errorText(error) {
            const body = (error && error.response && error.response.data) || error || {}
            const details = body.errors ? Object.keys(body.errors).reduce((all, key) => all.concat(body.errors[key]), []) : []
            return [body.message || 'Unable to process allocation.', ...details].join(' ')
        },
        reportSubmitError(error) {
            const message = this.errorText(error)
            this.errorMessage = message
            if (typeof console !== 'undefined' && console.error) {
                console.error('[BulkAllocationDialog] Save failed', error)
            }
            if (this.$ThatsNotify && this.$ThatsNotify.error) {
                this.$ThatsNotify.error({ message })
            }
        },
        addRows() {
            if (!this.spreadsheet || this.submitting) return
            const count = Math.min(10, 500 - this.spreadsheet.getData().length)
            if (count > 0) this.spreadsheet.insertRow(count)
        },
        removeSelectedRows() {
            if (!this.spreadsheet || this.submitting) return
            const selected = new Set(this.spreadsheet.getSelectedRows(true).map(Number))
            const data = this.spreadsheet.getData().filter((row, index) => !selected.has(index))
            this.spreadsheet.setData(data)
            this.selectedRowCount = 0
            this.scheduleRefresh()
        },
        clearForm() {
            if (this.submitting) return
            this.resetLookups()
            if (this.spreadsheet) {
                this.spreadsheet.setData([])
                this.refreshRows()
            }
        },
        recheckItems() {
            if (this.submitting || !this.spreadsheet) return
            this.resetLookups()
            this.refreshRows()
        },
        destroySpreadsheet() {
            this.resetLookups()
            if (this.spreadsheet) {
                this.spreadsheet.destroy()
                this.spreadsheet = null
            }
        },
        async submit() {
            if (this.submitting) return
            try {
                this.refreshRows()
                if (!this.canSave) {
                    this.errorMessage = 'Resolve the row messages before saving.'
                    return
                }
                await MessageBox.confirm('Apply these allocation quantities?', 'Bulk Set Allocation', {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: 'warning',
                })
            } catch (error) {
                if (error !== 'cancel' && error !== 'close') this.reportSubmitError(error)
                return
            }
            try {
                this.refreshRows()
                if (!this.canSave) {
                    this.errorMessage = 'Resolve the row messages before saving.'
                    return
                }
            } catch (error) {
                this.reportSubmitError(error)
                return
            }
            const submitted = this.rows.slice()
            this.submitting = true
            this.spreadsheet.options.editable = false
            this.errorMessage = ''
            this.saveMessages = {}
            try {
                const response = await this.bulkAllocation({
                    items: submitted.map(row => ({ item_id: row.lookup.item.id, total_allocation_quantity: Number(row.quantity) })),
                })
                if (!response || Number(response.error) === 1) throw response || new Error('No response received.')
                submitted.forEach(row => {
                    const saved = (response.data || []).find(item => Number(item.item_id) === Number(row.lookup.item.id))
                    if (saved) {
                        row.lookup.item.existing_remaining_allocated_quantity = Number(saved.allocated_quantity)
                        row.lookup.item.minimum_allocation_quantity = Number(saved.minimum_allocation_quantity)
                    }
                    this.$set(this.saveMessages, row.index, { success: true, message: response.message || 'Allocations successfully updated.' })
                })
                this.$ThatsNotify.success({ message: response.message || 'Allocations successfully updated.' })
                this.$emit('success')
            } catch (error) {
                const body = (error && error.response && error.response.data) || error || {}
                this.reportSubmitError(error)
                submitted.forEach((row, payloadIndex) => {
                    const errors = body.errors || {}
                    const messages = Object.keys(errors).filter(key => key === 'items.' + payloadIndex || key.startsWith('items.' + payloadIndex + '.'))
                        .reduce((all, key) => all.concat(errors[key]), [])
                    this.$set(this.saveMessages, row.index, { success: false, message: 'Not saved. ' + (messages.join(' ') || this.errorMessage) })
                })
            } finally {
                this.submitting = false
                if (this.spreadsheet) {
                    this.spreadsheet.options.editable = true
                    this.refreshRows()
                }
            }
        },
    },
}
</script>
